import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  CheckCircle,
  ArrowLeft,
  Receipt,
  // CreditCard,
  QrCode,
  ShoppingBag,
  AlertCircle,
  Download,
  Share2,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { authService } from "@/services";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import QRCode from "qrcode";
import { apiClient } from "@/services";
import { toast } from "@/hooks/useToast";
import { useCartStore } from "@/stores/cartStore";

/**
 * Página de éxito de pago
 *
 * Muestra la confirmación cuando el pago con Transbank fue exitoso
 * Obtiene los datos del pago desde el servidor usando un token seguro
 */
export function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>("");
  const [paymentData, setPaymentData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { clearCart } = useCartStore();

  // Obtener parámetros de la URL (nuevo sistema seguro y fallback)
  const sessionToken = searchParams.get("token");
  const source = searchParams.get("source");
  const isPopup = searchParams.get("popup") === "true";

  // Parámetros del sistema anterior (fallback)
  const orderId = searchParams.get("orderId");
  const amount = searchParams.get("amount");
  const authCode = searchParams.get("authCode");
  const itemsParam = searchParams.get("items");

  useEffect(() => {
    const initializePage = async () => {
      // Log completo de la URL y parámetros
      console.log("🔍 [PaymentSuccess] ===========================================");
      console.log("🔍 [PaymentSuccess] URL COMPLETA:", window.location.href);
      console.log("🔍 [PaymentSuccess] Parámetros recibidos:", {
        sessionToken: sessionToken || 'NULL',
        sessionTokenLength: sessionToken?.length || 0,
        orderId: orderId || 'NULL',
        amount: amount || 'NULL',
        authCode: authCode || 'NULL',
        source: source || 'NULL',
        isPopup: isPopup,
        hasItems: !!itemsParam,
      });
      console.log("🔍 [PaymentSuccess] ===========================================");

      // Validación temprana: Debe tener al menos un parámetro válido
      if (!sessionToken && !orderId) {
        console.error("❌ [PaymentSuccess] No hay parámetros de pago válidos");
        setError("Acceso no autorizado. No hay datos de pago.");
        setIsLoading(false);

        // Redirigir al home después de 3 segundos
        setTimeout(() => {
          navigate("/");
        }, 3000);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Debug: información sobre la ventana actual
        console.log("🔍 [Frontend PaymentSuccess] Información de la ventana:", {
          hasOpener: !!window.opener,
          openerClosed: window.opener ? window.opener.closed : 'N/A',
          windowName: window.name,
          isPopupParam: isPopup,
          isPopupByName: window.name === 'transbank_popup',
          referrer: document.referrer,
          isInIframe: window !== window.top,
        });

        // ✅ FLUJO SIMPLE: Ya no verificamos popup, solo procesamos el pago exitoso

        // Limpiar el carrito después de un pago exitoso (frontend y backend)
        try {
          console.log("🛒 [PaymentSuccess] Iniciando limpieza del carrito...");
          console.log("🛒 [PaymentSuccess] Estado del carrito ANTES de limpiar:", useCartStore.getState().items);

          await clearCart();

          console.log("🛒 [PaymentSuccess] Estado del carrito DESPUÉS de limpiar:", useCartStore.getState().items);
          console.log("✅ [PaymentSuccess] Carrito limpiado exitosamente del frontend y backend");
        } catch (error) {
          console.error("❌ [PaymentSuccess] Error limpiando carrito:", error);
          console.error("❌ [PaymentSuccess] Detalles del error:", JSON.stringify(error, null, 2));
          // No fallar la página si hay error al limpiar el carrito
        }

        // Si viene de Transbank y no tiene sesión, intentar restaurar la sesión
        if (source === "transbank" && !user) {
          try {
            console.log("🔄 Intentando restaurar sesión después del pago...");
            const authStatus = await authService.verifyAuthStatus();

            if (authStatus.isValid && authStatus.user) {
              console.log("✅ Sesión restaurada exitosamente");
            } else {
              console.log("❌ No se pudo restaurar la sesión");
            }
          } catch (error) {
            console.log("❌ Error al verificar sesión:", error);
          }
        }

        let paymentDataToSet = null;

        // Obtener datos del pago desde el servidor usando el token
        if (sessionToken) {
          console.log("🔄 Obteniendo datos del pago con token:", sessionToken);
          console.log("🔄 Token length:", sessionToken.length);

          try {
            const response = await apiClient.get(
              `/api/v1/payments/success-data/${sessionToken}`
            );

            if (response.data.success) {
              paymentDataToSet = response.data.data;
              console.log("✅ Datos del pago obtenidos:", paymentDataToSet);
              console.log(
                "✅ OrderId en la respuesta:",
                paymentDataToSet.orderId
              );
            } else {
              console.log(
                "⚠️ No se pudieron obtener los datos del pago, usando fallback"
              );
              // Usar fallback si el servidor no puede proporcionar los datos
              throw new Error("Server data not available");
            }
          } catch (error: any) {
            console.log(
              "⚠️ Error obteniendo datos del servidor, usando fallback:",
              error.message
            );

            // Si hay error 429 o cualquier otro error, usar fallback
            if (orderId && amount) {
              console.log("🔄 Usando datos del sistema anterior como fallback");

              const cartItems = itemsParam
                ? JSON.parse(decodeURIComponent(itemsParam))
                : [];

              const subtotal = Math.round(parseInt(amount) / 1.19);
              const iva = parseInt(amount) - subtotal;

              paymentDataToSet = {
                orderId,
                amount: parseInt(amount),
                authCode,
                items: cartItems,
                subtotal,
                iva,
              };

              console.log("✅ Datos del pago obtenidos del fallback:", {
                orderId,
                amount,
                itemCount: cartItems.length,
              });
            } else {
              setError("No se pudieron obtener los datos del pago");
            }
          }
        } else if (orderId && amount) {
          // Fallback al sistema anterior
          console.log("🔄 Usando datos del sistema anterior como fallback");

          const cartItems = itemsParam
            ? JSON.parse(decodeURIComponent(itemsParam))
            : [];

          const subtotal = Math.round(parseInt(amount) / 1.19);
          const iva = parseInt(amount) - subtotal;

          paymentDataToSet = {
            orderId,
            amount: parseInt(amount),
            authCode,
            items: cartItems,
            subtotal,
            iva,
          };

          console.log("✅ Datos del pago obtenidos del fallback:", {
            orderId,
            amount,
            itemCount: cartItems.length,
          });
        } else {
          setError("No se encontraron datos válidos del pago");
        }

        // Establecer los datos del pago
        if (paymentDataToSet) {
          setPaymentData(paymentDataToSet);

          // Generar QR code con los datos obtenidos
          try {
            const qrData = {
              orderId: paymentDataToSet.orderId,
              amount: paymentDataToSet.amount,
              amountWithIVA: paymentDataToSet.amount,
              subtotal: paymentDataToSet.subtotal,
              iva: paymentDataToSet.iva,
              authCode: paymentDataToSet.authCode,
              timestamp: Date.now(),
              userId: user?.id || "anonymous",
            };

            // Codificar los datos del QR en base64 para consistencia
            const qrDataString = JSON.stringify(qrData);
            const qrDataBase64 = btoa(
              unescape(encodeURIComponent(qrDataString))
            );

            const qrCodeDataUrl = await QRCode.toDataURL(qrDataBase64, {
              width: 200,
              margin: 2,
              color: {
                dark: "#000000",
                light: "#FFFFFF",
              },
            });

            setQrCodeDataUrl(qrCodeDataUrl);
            console.log("✅ QR code generado (base64):", qrData);
            console.log("📝 Datos codificados:", qrDataBase64);
          } catch (error) {
            console.error("❌ Error generando QR code:", error);
          }
        }
      } catch (error) {
        console.error("❌ Error inicializando página:", error);
        setError("Error al cargar los datos del pago");
      } finally {
        // Simular carga de datos
        const timer = setTimeout(() => {
          setIsLoading(false);
        }, 1000);

        return () => clearTimeout(timer);
      }
    };

    initializePage();
  }, [source, user, sessionToken, orderId, amount, authCode, itemsParam, isPopup]);

  // Funciones auxiliares
  const formatPrice = (price: number | string | null) => {
    if (!price) return "N/A";
    const numericPrice = typeof price === "string" ? parseInt(price) : price;
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numericPrice);
  };


  // Si hay error, mostrar mensaje
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 dark:from-red-950 dark:to-red-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-700 dark:text-red-300 mb-2">
              Error al cargar el pago
            </h2>
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <Button onClick={() => navigate("/")} className="w-full">
              Volver al Inicio
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Si está cargando, mostrar skeleton
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950 dark:to-green-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
          <CardContent className="p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-gray-200 rounded w-1/3"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-32 bg-gray-200 rounded"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Si no hay datos del pago, mostrar error
  if (!paymentData) {
    // Validación: No se puede acceder a esta página sin datos válidos
    console.warn("⚠️ [PaymentSuccess] Acceso sin datos de pago válidos");

    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-950 dark:to-yellow-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-yellow-700 dark:text-yellow-300 mb-2">
              Acceso no autorizado
            </h2>
            <p className="text-yellow-600 dark:text-yellow-400 mb-4">
              No se pudieron obtener los datos del pago. Esta página solo es
              accesible después de completar un pago exitoso.
            </p>
            <Button onClick={() => navigate("/")} className="w-full">
              Volver al Inicio
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Desglose del monto eliminado - ya no se calcula IVA

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Contenido principal con efecto wow */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <div className="w-full max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
            {/* Columna Izquierda - Información de éxito */}
            <div className="flex flex-col justify-center">
              {/* Hero Section */}
              <div className="text-center lg:text-left mb-6">
                <Badge className="bg-green-500/20 text-green-600 border-green-500/30 px-4 py-2 rounded-full mb-4">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  ¡Pago Exitoso!
                </Badge>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4 leading-tight">
                  ¡Transacción{" "}
                  <span className="bg-gradient-to-r from-green-500 to-green-600 bg-clip-text text-transparent">
                    Completada!
                  </span>
                </h1>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Tu pago ha sido procesado correctamente. Ya puedes retirar tu
                  pedido usando el código QR.
                </p>
              </div>

              {/* Card de éxito con efecto glassmorphism */}
              <Card className="shadow-lg hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-card/80 to-card/60 backdrop-blur-xl">
                <CardContent className="p-6">
                  <div className="text-center space-y-4">
                    <div className="flex justify-center mb-4">
                      <div className="bg-gradient-to-br from-green-500/20 to-green-600/20 p-4 rounded-full border border-green-500/30">
                        <CheckCircle className="h-12 w-12 text-green-600" />
                      </div>
                    </div>
                    <h2 className="text-xl font-bold text-foreground">
                      ¡Pago Confirmado!
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Tu transacción ha sido procesada exitosamente
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Columna Derecha - Detalles de la transacción */}
            <div className="flex flex-col justify-center space-y-4">
              {/* Detalles de la transacción con efecto wow */}
              <Card className="shadow-lg hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-card/80 to-card/60 backdrop-blur-xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-foreground">
                    <Receipt className="h-5 w-5 text-primary" />
                    Detalles de la Transacción
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-3 rounded-lg bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20">
                      <label className="text-xs font-medium text-muted-foreground">
                        ID de Pedido
                      </label>
                      <p className="text-lg font-semibold text-foreground">
                        {paymentData.orderId || "N/A"}
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20">
                      <label className="text-xs font-medium text-muted-foreground">
                        Código de Autorización
                      </label>
                      <p className="text-lg font-semibold font-mono text-foreground">
                        {paymentData.authCode || "N/A"}
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-gradient-to-br from-green-500/5 to-green-600/10 border border-green-500/20">
                      <label className="text-xs font-medium text-muted-foreground">
                        Monto Pagado
                      </label>
                      <p className="text-2xl font-bold text-green-600">
                        {formatPrice(paymentData.amount)}
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-gradient-to-br from-green-500/5 to-green-600/10 border border-green-500/20">
                      <label className="text-xs font-medium text-muted-foreground">
                        Estado
                      </label>
                      <Badge className="bg-green-500/20 text-green-600 border-green-500/30">
                        Pagado
                      </Badge>
                    </div>
                  </div>

                  <Separator className="bg-border/50" />

                  {/* Desglose del monto */}
                  <div className="p-4 rounded-lg bg-gradient-to-br from-muted/30 to-muted/10 border border-border/50">
                    <h4 className="font-medium mb-3 text-foreground">
                      Desglose del Pago
                    </h4>
                    <div className="space-y-2 text-sm">
                      <Separator className="bg-border/30" />
                      <div className="flex justify-between font-semibold text-lg">
                        <span className="text-foreground">Total:</span>
                        <span className="text-green-600">
                          {formatPrice(paymentData.amount)}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Items del carrito con efecto wow */}
              {paymentData.items && paymentData.items.length > 0 && (
                <Card className="shadow-lg hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-card/80 to-card/60 backdrop-blur-xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-foreground">
                      <ShoppingBag className="h-5 w-5 text-primary" />
                      Productos Comprados
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {paymentData.items.map((item: any, index: number) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-4 rounded-lg bg-gradient-to-br from-muted/30 to-muted/10 border border-border/50 hover:border-primary/30 transition-all duration-200 hover:shadow-md"
                        >
                          <div className="flex items-center gap-3">
                            {item.image && (
                              <img
                                src={item.image}
                                alt={item.name}
                                className="w-12 h-12 object-cover rounded-lg border border-border/50"
                              />
                            )}
                            <div>
                              <p className="font-medium text-foreground">
                                {item.name}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                Cantidad: {item.quantity}
                              </p>
                            </div>
                          </div>
                          <p className="font-semibold text-foreground">
                            {formatPrice(item.price * item.quantity)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Código QR para canje con efecto wow */}
              {qrCodeDataUrl && (
                <Card className="shadow-lg hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-card/80 to-card/60 backdrop-blur-xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-foreground">
                      <QrCode className="h-5 w-5 text-primary" />
                      Código QR para Retirar
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <div className="flex flex-col items-center space-y-6">
                      <div className="bg-gradient-to-br from-white to-gray-50 p-6 rounded-2xl shadow-xl border border-border/50 hover:shadow-2xl transition-all duration-300">
                        <img
                          src={qrCodeDataUrl}
                          alt="QR Code"
                          className="w-48 h-48 rounded-lg"
                        />
                      </div>
                      <p className="text-sm text-muted-foreground max-w-md">
                        Muestra este código QR al cajero para retirar tu pedido
                      </p>

                      {/* Botones de acción con efecto wow */}
                      <div className="flex gap-3 mt-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const link = document.createElement("a");
                            link.href = qrCodeDataUrl;
                            link.download = `qr-pedido-${
                              paymentData?.orderId || "order"
                            }.png`;
                            link.click();
                          }}
                          className="flex items-center gap-2 bg-gradient-to-r from-primary/10 to-primary/5 hover:from-primary/20 hover:to-primary/10 border-primary/30 text-primary hover:text-primary/80 transition-all duration-200 hover:scale-105"
                        >
                          <Download className="h-4 w-4" />
                          Descargar QR
                        </Button>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (navigator.share) {
                              navigator.share({
                                title: "Código QR para retirar pedido",
                                text: `Tu código QR para retirar el pedido ${paymentData?.orderId}`,
                                url: window.location.href,
                              });
                            } else {
                              // Fallback: copiar URL al portapapeles
                              navigator.clipboard.writeText(
                                window.location.href
                              );
                              toast({
                                title: "URL copiada",
                                description:
                                  "La URL del pedido ha sido copiada al portapapeles",
                              });
                            }
                          }}
                          className="flex items-center gap-2 bg-gradient-to-r from-primary/10 to-primary/5 hover:from-primary/20 hover:to-primary/10 border-primary/30 text-primary hover:text-primary/80 transition-all duration-200 hover:scale-105"
                        >
                          <Share2 className="h-4 w-4" />
                          Compartir
                        </Button>
                      </div>

                      <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 p-4 rounded-xl border border-blue-500/20 max-w-md">
                        <p className="text-xs text-blue-600 dark:text-blue-400">
                          💡 <strong>Consejo:</strong> Descarga el QR para
                          tenerlo disponible cuando vayas a retirar tu pedido.
                          También puedes acceder a él desde tu historial de
                          pedidos.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Acciones con efecto wow */}
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <Button
                  onClick={() => navigate("/")}
                  className="flex-1 h-12 text-sm font-medium rounded-xl bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 transition-all duration-200 hover:scale-105 hover:shadow-lg"
                  size="lg"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Volver al Inicio
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    if (!user) {
                      navigate("/login");
                      return;
                    }
                    
                    // Determinar el dashboard correcto según el rol
                    if (user.roles?.includes("Estudiante")) {
                      navigate("/student/dashboard");
                    } else if (user.roles?.includes("Apoderado")) {
                      navigate("/dashboard");
                    } else if (user.roles?.includes("Admin")) {
                      navigate("/admin/dashboard");
                    } else if (user.roles?.includes("Cajero")) {
                      navigate("/cajero/dashboard");
                    } else {
                      navigate("/dashboard"); // Por defecto
                    }
                  }}
                  className="flex-1 h-12 text-sm font-medium rounded-xl bg-gradient-to-r from-muted/50 to-muted/30 hover:from-muted/70 hover:to-muted/50 border-border/50 transition-all duration-200 hover:scale-105 hover:shadow-lg"
                  size="lg"
                >
                  {user ? "Ver Mis Pedidos" : "Iniciar Sesión"}
                </Button>
              </div>

              {/* Información Adicional con efecto wow */}
              <div className="mt-8 text-center">
                <div className="bg-gradient-to-br from-muted/30 to-muted/10 p-4 rounded-xl border border-border/50">
                  <p className="text-sm text-muted-foreground">
                    Recibirás un correo de confirmación con los detalles de tu
                    pedido.
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Si tienes alguna pregunta, contacta a nuestro soporte.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
